import { DrugCategory } from '@/constants/programs';
import { InsuranceType } from '@/pages/members/constants/insurance';
import isRunningInReactNativeWebView from '@/utils/isRunningInReactNativeWebView';

import OnboardingEventBit from './onboardingEventBit';

const APP_EVENT_PREFIX = 'app_event';
const WEB_EVENT_PREFIX = 'web_event';

export interface OnboardingTrackingEvent {
  name: string;
  args?: Record<string, unknown>;
}

const insuranceEventMapping: Partial<{ [k in InsuranceType]: string }> = {
  [InsuranceType.EMPLOYER]: OnboardingEventBit.CommerciallyInsured,
  [InsuranceType.MARKETPLACE]: OnboardingEventBit.CommerciallyInsured,
  [InsuranceType.MEDICAID]: OnboardingEventBit.Medicaid,
  [InsuranceType.MEDICARE]: OnboardingEventBit.Medicare
};

const drugCategoryEventMapping: Record<DrugCategory, string> = {
  [DrugCategory.alcohol]: OnboardingEventBit.Alcohol,
  [DrugCategory.cannabis]: OnboardingEventBit.Cannabis,
  [DrugCategory.opioids]: OnboardingEventBit.Opioids,
  [DrugCategory.stimulants]: OnboardingEventBit.Stimulants,
  [DrugCategory.mental_health]: OnboardingEventBit.MentalHealth
};

enum DrugName {
  // this enum is just a helper for the odd events below. DO NOT USE IT OUTSIDE OF THIS FILE
  Alcohol = 'Alcohol',
  Crack = 'Crack',
  Cocaine = 'Cocaine',
  Meth = 'Meth',
  RxStimulantsADHDMeds = 'Rx stimulants/ADHD meds',
  WeedCannabis = 'Weed / Cannabis',
  Fentanyl = 'Fentanyl',
  Heroin = 'Heroin',
  RxPainkillers = 'Rx Painkillers',
  OtherOpioids = 'Other Opioids'
}

const substanceClassicEventMapping: Record<DrugName, string> = {
  // at the time of writing, these events are fired from jotform and we are choosing to
  // keep them because of google ads even though they are not documented and are oddly
  // named.
  [DrugName.Alcohol]: 'web_event_id_A',
  [DrugName.Crack]: 'web_event_id_B',
  [DrugName.Cocaine]: 'web_event_id_B',
  [DrugName.Meth]: 'web_event_id_B',
  [DrugName.RxStimulantsADHDMeds]: 'web_event_id_B',
  [DrugName.WeedCannabis]: 'web_event_id_W',
  [DrugName.Fentanyl]: 'web_event_id_O',
  [DrugName.Heroin]: 'web_event_id_O',
  [DrugName.RxPainkillers]: 'web_event_id_O',
  [DrugName.OtherOpioids]: 'web_event_id_O'
};

const insuranceClassicEventMapping: Partial<{ [k in InsuranceType]: string[] }> = {
  // at the time of writing, these events are fired from jotform and we are choosing to
  // keep them because of google ads even though they are not documented and are oddly
  // named.
  [InsuranceType.MEDICAID]: ['web_event_id_C'],
  [InsuranceType.MEDICARE]: ['web_event_id_F'],
  [InsuranceType.EMPLOYER]: ['web_event_id_D', 'web_event_id_E'],
  [InsuranceType.MARKETPLACE]: ['web_event_id_D', 'web_event_id_M']
};

const gtmOnboardingEventBuilder = (
  insuranceType: InsuranceType,
  treatable: boolean,
  jurisdictionActive: boolean,
  drugCategory: DrugCategory,
  substanceName: string,
  pltv?: number | null
): OnboardingTrackingEvent[] => {
  const runningInWebView = isRunningInReactNativeWebView();
  const onboardingValueEvent: OnboardingTrackingEvent = {
    name: 'onboarding_value',
    args: {
      value: pltv ?? null,
      currency: 'USD'
    }
  };

  if (!jurisdictionActive) {
    return [onboardingValueEvent];
  }

  const eventPrefix = runningInWebView ? APP_EVENT_PREFIX : WEB_EVENT_PREFIX;
  const eventNames: string[] = [];

  const insuranceBit = insuranceEventMapping[insuranceType] || '';
  if (!insuranceBit) {
    // if insurance is not mapped (i.e. NO_INSURANCE or UNKNOWN), just return early
    return [onboardingValueEvent];
  }

  const substanceBit = drugCategoryEventMapping[drugCategory] || '';

  eventNames.push(`${eventPrefix}_${insuranceBit}`);

  if (substanceBit) {
    eventNames.push(`${eventPrefix}_${insuranceBit}_${substanceBit}`);
  }

  if (treatable) {
    eventNames.push(`${eventPrefix}_${insuranceBit}_${OnboardingEventBit.Treatable}`);

    if (substanceBit) {
      eventNames.push(
        `${eventPrefix}_${insuranceBit}_${substanceBit}_${OnboardingEventBit.Treatable}`
      );
    }
  }

  if (!runningInWebView) {
    eventNames.push(...buildClassicEvents(insuranceType, substanceName as DrugName));
  }

  return [onboardingValueEvent, ...eventNames.map((name) => ({ name }))];
};

const buildClassicEvents = (insuranceType: InsuranceType, substanceName: DrugName): string[] => {
  const events: string[] = [];

  const substanceEvent = substanceClassicEventMapping[substanceName];
  if (substanceEvent && insuranceType !== InsuranceType.NO_INSURANCE) {
    // originally jotform checks if insurance type or the insurace provider is "I don't have insurance"
    // however, we don't offer that option for insurance provider so we need just check insurance type
    events.push(substanceEvent);
  }

  const insuranceEvents = insuranceClassicEventMapping[insuranceType];
  if (insuranceEvents) {
    events.push(...insuranceEvents);
  }

  return events;
};

export default gtmOnboardingEventBuilder;
